import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import React from 'react';

import type { GetReportDataFilterParams } from 'app/Domain/Reports/Services/ReportService';
import { reportService } from 'app/Domain/Reports/Services/ReportService';
import { useSnakeBar } from 'app/hooks/useSnakeBar';
import type { UserDataFilterResponse } from 'app/types/report.types';
import type { UseQueryWrapper } from 'app/types/useQuery.types';

type GetReportDataFilterQueryResultData = UserDataFilterResponse;

type GetReportDataFilterQueryVariables = {
  [Key in keyof GetReportDataFilterParams]: GetReportDataFilterParams[Key] | undefined;
};

const queryFn =
  ({ userId, page, limit, searchQuery }: GetReportDataFilterQueryVariables) =>
  async () => {
    if (userId) {
      const response = await reportService.getReportDataFilter({
        userId,
        page: page ?? 1,
        limit: limit ?? 25,
        searchQuery: searchQuery ?? undefined,
      });
      return (response as AxiosResponse).data;
    }
  };

const QUERY_KEY = 'getUserReportDataFilter';

export const useReportsGetDataFilterQuery: UseQueryWrapper<
  GetReportDataFilterQueryResultData,
  GetReportDataFilterQueryVariables
> = ({ variables, options }) => {
  const { showErrorSnakeBar } = useSnakeBar();

  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: QUERY_KEY });
  }, [showErrorSnakeBar]);

  return useQuery<GetReportDataFilterQueryResultData>(
    [QUERY_KEY, variables.userId, variables.page, variables.limit, variables.searchQuery],
    queryFn(variables),
    {
      onError,
      keepPreviousData: true,
      ...(options || {}),
    },
  );
};
